import TopLevelMethod from "./TopLevelMethod";
import ViewerMethod from "./ViewerMethod";
import {
  getEventActionName,
  getFailActionName,
  getStartActionName,
  getSuccessActionName,
} from "./make-action";
import { combineReducers } from "redux";
import ViewerEvent from "./ViewerEvent";
import { LoadStatusWithFetchDetail } from "../../../../../common/utils/async-status";

export interface FetchState {
  status: LoadStatusWithFetchDetail;
  args: any[];
}

export interface ResultState {
  data: any;
  args: any[];
  isOk: boolean;
  resultStatus?: number;
}

export interface ReducerState {
  fetchState: FetchState;
  resultState: ResultState;
}

const initialFetchState = {
  status: LoadStatusWithFetchDetail.WAITING,
  args: undefined,
};

const initialResultState = {
  data: undefined,
  args: undefined,
  isOk: undefined,
};

function makeFetchReducer(apiCall) {
  return function (state = initialFetchState, action) {
    switch (action.type) {
      case getStartActionName(apiCall):
        return {
          status: LoadStatusWithFetchDetail.FETCHING,
          args: action.apiCallArgs,
        };
      case getSuccessActionName(apiCall):
        return {
          status: LoadStatusWithFetchDetail.DONE,
          args: action.apiCallArgs,
        };
      case getFailActionName(apiCall):
        return {
          status: LoadStatusWithFetchDetail.DONE,
          args: action.apiCallArgs,
        };
      default:
        return state;
    }
  };
}

function makeResultReducer(apiCall, processResult, resultDataDefaultState?) {
  const defaultState = {
    ...initialResultState,
    data: resultDataDefaultState,
  };
  return function (state = defaultState, action) {
    switch (action.type) {
      case getSuccessActionName(apiCall):
        return {
          isOk: true,
          data: processResult(action.resultData),
          args: action.apiCallArgs,
        };
      case getFailActionName(apiCall):
        return {
          isOk: false,
          data: undefined,
          args: action.apiCallArgs,
          resultStatus: action.resultStatus,
        };
      default:
        return state;
    }
  };
}

/**
 * This creates a simple reducer for async jigsaw fetch operations that are
 * handled via the middleware. The split between fetchState and resultState
 * allows you to refetch/refresh while keeping the old results active
 * @param apiCall
 * @param processResult E.g. data normalization function
 * @param resultDataDefaultState Default value for
 */
export function makeReducer(
  apiCall: TopLevelMethod | ViewerMethod,
  processResult = (data) => data,
  resultDataDefaultState?
) {
  return combineReducers({
    fetchState: makeFetchReducer(apiCall),
    resultState: makeResultReducer(
      apiCall,
      processResult,
      resultDataDefaultState
    ),
  });
}

export function makeEventReducer(eventName: ViewerEvent) {
  return function (state = {}, action) {
    switch (action.type) {
      case getEventActionName(eventName):
        return action.eventData;
      default:
        return state;
    }
  };
}
